import { useTokensFromList } from 'hooks/portfolio/useTokenList'
import React, { createContext, useContext, useMemo } from 'react'
import { normalizeDisplaySymbol } from 'utils/portfolio/filterHelpers'
import { Address } from 'viem'

interface TokenData {
  logoUri?: string
  decimals: number
  symbol: string
  name: string
}

interface TokenDataContextValue {
  tokenData: Record<Address, TokenData>
  isLoading: boolean
}

const TokenDataContext = createContext<TokenDataContextValue | undefined>(undefined)

export function TokenDataProvider({ children }: { children: React.ReactNode }) {
  const { data: tokens, isLoading } = useTokensFromList()

  const tokenData = useMemo(() => {
    const map: Record<Address, TokenData> = {}

    tokens?.forEach((token) => {
      const address = token.address.toLowerCase() as Address
      map[address] = {
        logoUri: token.logoUri,
        decimals: token.decimals,
        symbol: normalizeDisplaySymbol(token.symbol),
        name: token.name,
      }
    })

    return map
  }, [tokens])

  return <TokenDataContext.Provider value={{ tokenData, isLoading }}>{children}</TokenDataContext.Provider>
}

export function useTokenData() {
  const context = useContext(TokenDataContext)
  if (!context) {
    throw new Error('useTokenData must be used within TokenDataProvider')
  }
  return context
}

export function enrichTokenWithData<T extends { id?: string; symbol?: string }>(
  token: T | null | undefined,
  tokenData: Record<Address, TokenData>
): T & { logoUri?: string; symbol?: string } {
  if (!token || !token.id) {
    return token as T & { logoUri?: string; symbol?: string }
  }

  const address = token.id.toLowerCase() as Address
  const data = tokenData[address]
  return {
    ...token,
    logoUri: data?.logoUri,
    ...(token.symbol && { symbol: data?.symbol ?? normalizeDisplaySymbol(token.symbol) }),
  }
}
